import React, { useState, useEffect } from 'react';
import { Field, ErrorMessage, useFormikContext } from 'formik';
import InputMask from 'react-input-mask-next';
import { fetchZipCodeData } from '../../../utils/Functions/zipCodeService';

const AddressSection = () => {
  const { values, setFieldValue } = useFormikContext();
  const [loading, setLoading] = useState(false);
  const [cepError, setCepError] = useState(null);
  const [touchedZip, setTouchedZip] = useState(false);

  useEffect(() => {
    const handleZipCodeChange = async () => {
      const cleanedZip = values.address?.zipcode?.replace(/\D/g, '');

      // Limpa campos quando CEP está incompleto
      if (cleanedZip?.length < 8) {
        if (touchedZip) {
          setFieldValue('address.street', '');
          setFieldValue('address.neighborhood', '');
          setFieldValue('address.city', '');
          setFieldValue('address.uf', '');
          setFieldValue('address.number', '');
          setFieldValue('address.complement', '');
        }
        return;
      }

      if (cleanedZip?.length === 8) {
        try {
          setLoading(true);
          setCepError(null);

          const addressData = await fetchZipCodeData(values.address.zipcode);

          setFieldValue('address.street', addressData.street);
          setFieldValue('address.neighborhood', addressData.neighborhood);
          setFieldValue('address.city', addressData.city);
          setFieldValue('address.uf', addressData.state);

          document.getElementById('address.number')?.focus();
        } catch (error) {
          if (error.message === 'CEP_NOT_FOUND') {
            setCepError('CEP não encontrado. Por favor, preencha os dados manualmente.');
            // Limpa apenas se não tiver dados digitados
            if (!values.address.street) {
              setFieldValue('address.street', '');
              setFieldValue('address.neighborhood', '');
              setFieldValue('address.city', '');
              setFieldValue('address.uf', '');
              setFieldValue('address.number', '');
              setFieldValue('address.complement', '');
            }
          }
        } finally {
          setLoading(false);
        }
      }
    };

    const timer = setTimeout(handleZipCodeChange, 800); // Debounce de 800ms
    return () => clearTimeout(timer);
  }, [values.address?.zipcode, setFieldValue, touchedZip]);

  // Componente Skeleton para loading
  const SkeletonField = ({ height = '38px' }) => (
    <div
      className="skeleton-field"
      style={{
        height,
        width: '100%',
        backgroundColor: '#8bc540',
        borderRadius: '4px',
        animation: 'pulse 1.5s infinite ease-in-out'
      }}
    />
  );

  return (
    <>
      <h5>Endereço</h5>

      <div className="patient-form-group">
        <label htmlFor="address.zipcode">CEP:</label>
        <div className={`input-container ${loading ? 'loading' : ''}`}>
          <Field name="address.zipcode">
            {({ field, meta }) => (
              <InputMask
                {...field}
                id="address.zipcode"
                mask="99999-999"
                placeholder="Digite o CEP"
                className={meta.touched && meta.error ? 'error' : ''}
                onBlur={(e) => {
                  setTouchedZip(true);
                  const cleanedCEP = e.target.value.replace(/\D/g, '');
                  if (cleanedCEP.length === 8) {
                    setFieldValue('address.zipcode', cleanedCEP);
                  }
                }}
                onChange={(e) => {
                  setFieldValue("address.zipcode", e.target.value);
                  setTouchedZip(true);
                }}
              />
            )}
          </Field>
          {loading && <div className="loading-indicator"></div>}
        </div>
        {cepError && <div className="cep-error-message">{cepError}</div>}
        <ErrorMessage name="address.zipcode" component="div" className="cep-error-message" />
      </div>

      {loading ? (
        <>
          <div className="patient-form-group">
            <label>Rua:</label>
            <SkeletonField />
          </div>
          <div className="patient-form-group">
            <label>Bairro:</label>
            <SkeletonField />
          </div>
          <div className="patient-form-group">
            <label>Cidade:</label>
            <SkeletonField />
          </div>
          <div className="patient-form-group">
            <label>UF:</label>
            <SkeletonField height="42px" />
          </div>
        </>
      ) : (
        <>
          <div className="patient-form-group">
            <label htmlFor="address.street">Rua:</label>
            <Field
              type="text"
              name="address.street"
              id="address.street"
              placeholder="Digite a rua"
            />
            <ErrorMessage name="address.street" component="div" className="error-message" />
          </div>
          <div className="patient-form-group">
            <label htmlFor="address.number">Número</label>
            <Field
              type="text"
              name="address.number"
              id="address.number"
              placeholder="Digite o número"
            />
            <ErrorMessage name="address.number" component="div" className="error-message" />
          </div>

          <div className="patient-form-group">
            <label htmlFor="address.complement">Complemento:</label>
            <Field
              type="text"
              name="address.complement"
              id="address.complement"
              placeholder="Digite o complemento"
            />
          </div>

          <div className="patient-form-group">
            <label htmlFor="address.neighborhood">Bairro:</label>
            <Field
              type="text"
              name="address.neighborhood"
              id="address.neighborhood"
              placeholder="Digite o bairro"
            />
          </div>

          <div className="patient-form-group">
            <label htmlFor="address.city">Cidade:</label>
            <Field
              type="text"
              name="address.city"
              id="address.city"
              placeholder="Digite a cidade"
            />
          </div>

          <div className="patient-form-group">
            <label htmlFor="address.uf">UF:</label>
            <Field
              as="select"
              name="address.uf"
              id="address.uf"
            >
              <option value="">Selecione</option>
              <option value="AC">Acre - AC</option>
              <option value="AL">Alagoas - AL</option>
              <option value="AM">Amazonas - AM</option>
              <option value="AP">Amapá - AP</option>
              <option value="BA">Bahia - BA</option>
              <option value="CE">Ceará - CE</option>
              <option value="DF">Distrito Federal - DF</option>
              <option value="ES">Espírito Santo - ES</option>
              <option value="GO">Goiás - GO</option>
              <option value="MA">Maranhão - MA</option>
              <option value="MG">Minas Gerais - MG</option>
              <option value="MS">Mato Grosso do Sul - MS</option>
              <option value="MT">Mato Grosso - MT</option>
              <option value="PA">Pará - PA</option>
              <option value="PB">Paraíba - PB</option>
              <option value="PE">Pernambuco - PE</option>
              <option value="PI">Piauí - PI</option>
              <option value="PR">Paraná - PR</option>
              <option value="RJ">Rio de Janeiro - RJ</option>
              <option value="RN">Rio Grande do Norte - RN</option>
              <option value="RO">Rondônia - RO</option>
              <option value="RR">Roraima - RR</option>
              <option value="RS">Rio Grande do Sul - RS</option>
              <option value="SC">Santa Catarina - SC</option>
              <option value="SE">Sergipe - SE</option>
              <option value="SP">São Paulo - SP</option>
              <option value="TO">Tocantins - TO</option>
            </Field>
          </div>
        </>
      )}

    </>
  );
};

export default AddressSection;